/** Single source for the Documents release-flag key (camelCased by the LD
 *  SDK); the nav entries and the route gate must never disagree. */
export const DOCUMENTS_RELEASE_FLAG = "releaseTechReviewDocuments";

export type InspectionNavItem = {
  key: string;
  label: string;
  url: string;
  icon: string;
  /** LaunchDarkly flag (camelCase key) gating this entry; absent = always shown. */
  flag?: string;
};

/**
 * The inspection pages, in the display order set by the Tech Review App
 * Migration nav design (Details, Checklist, Photos, Documents, Defects,
 * Grading, Conditions, Reports, Adjusted Grade — for the pages that exist).
 * This drives both the burger sidebar and the Home menubar so the two
 * navigation surfaces can't drift apart. Every route here needs an
 * inspection id — without one the built URLs are malformed — so the menu
 * offers nothing until an id is known.
 */
export const getInspectionNavItems = (
  inspectionId: string
): InspectionNavItem[] => {
  if (!inspectionId) {
    return [];
  }

  return [
    {
      key: "checklist",
      label: "Checklist",
      url: `/inspection/answers/${inspectionId}`,
      icon: "icon-checklist-review",
    },
    {
      key: "documents",
      label: "Documents",
      url: `/inspection/${inspectionId}/documents`,
      icon: "icon-documents",
      flag: DOCUMENTS_RELEASE_FLAG,
    },
    {
      key: "defects",
      label: "Defects",
      url: `/inspection/${inspectionId}/defects`,
      icon: "pi pi-exclamation-triangle",
    },
    {
      key: "grading",
      label: "Grading",
      url: `/inspection/${inspectionId}/grading`,
      icon: "pi pi-star",
    },
    {
      key: "conditions",
      label: "Conditions",
      url: `/inspection/${inspectionId}/ai-summary`,
      icon: "pi pi-file-edit",
    },
    {
      key: "adjustedGrade",
      label: "Adjusted Grade",
      url: `/inspection/${inspectionId}/adjusted`,
      icon: "pi pi-sliders-h",
    },
  ];
};

/**
 * Applies each entry's release flag against the LaunchDarkly flag set. Both
 * navigation surfaces must filter through this so a flagged page can't appear
 * in one and not the other.
 */
export const filterNavItemsByFlags = (
  items: InspectionNavItem[],
  flags: Record<string, boolean | undefined>
): InspectionNavItem[] =>
  items.filter((item) => !item.flag || flags[item.flag]);
